import { ApiError, fetchWithAuth, getJson, readBody } from "./client";

// Archive description document — the assembled view the operator builds
// from each month's cached _description.json plus human errata, served at
// /api/v1/operator/archive/description. Distinct from the per-export
// "dataset" the wizard describes: this describes THE ARCHIVE (the source
// corpus). See docs/plans/archive-description.md.

export interface ArchiveTimelineRow {
  month: string; // "YYYY-MM"
  pages: number;
  duration_hours: number;
  distinct_stations: number | null;
  days_present: number | null;
  days_in_month: number | null;
  missing_days: number; // count of missing days that month
  auto_flags: number; // count of low-coverage flags that month
}

export interface ArchiveNetwork {
  source_id: string;
  source_name: string | null;
  pages: number;
  months_present: number;
  first_month: string;
  last_month: string;
}

export interface ArchiveCategory {
  category: string | null;
  pages: number;
  duration_hours: number;
}

// Podcast-only aggregation (null on broadcast). Episodes by language.
export interface ArchiveLanguage {
  language: string | null;
  documents: number;
  duration_hours: number;
}

// One machine-generated arrival/departure remark.
export interface StationRemark {
  kind: string; // "station_arrived" | "station_departed"
  source_id: string;
  source_name: string | null;
  month: string; // "YYYY-MM"
  text: string; // human-readable line, ready to render
  first_utc?: string | null;
  last_utc?: string | null;
  trailing_months?: number;
}

// A single human-entered note. Free-form beyond `text`; we keep the
// optional attribution fields the editor sets.
export interface MonthNote {
  text: string;
  author?: string;
  created_at?: string;
}

export interface ArchiveErratum {
  content_type: string;
  year: string;
  month: string;
  notes: MonthNote[];
  updated_at: number | null;
}

// A station-outage remark (a station dark for a stretch of whole covered
// months). `months` is the fully-absent-month count the detector gated on;
// `days` is the observable last-page → first-page span.
export interface OutageRemark {
  kind: string;
  source_id: string;
  source_name: string | null;
  start: string; // ISO
  end: string; // ISO
  days: number;
  months: number;
  text: string;
}

export interface ArchiveDescription {
  content_type: string;
  generated_at: string;
  months_covered: number;
  coverage: { first_month: string | null; last_month: string | null };
  totals: {
    pages: number;
    duration_hours: number;
    superdocuments: number | null;
    distinct_stations: number;
  };
  timeline: ArchiveTimelineRow[];
  by_network: ArchiveNetwork[];
  by_network_truncated: boolean;
  by_category: ArchiveCategory[];
  // Podcast-only (null on broadcast). Present so the corpus switcher can
  // render podcast's own framing.
  by_language?: ArchiveLanguage[] | null;
  by_explicit?: Record<string, number> | null;
  // Kept for back-compat, but the lists now arrive EMPTY — the full remark
  // sets moved to the paged /archive/remarks endpoint (podcast produced
  // ~348k lifecycle remarks that bloated this doc to 105MB). Use
  // `auto_remarks_summary` for counts and `getArchiveRemarks` for the lists.
  auto_remarks: {
    arrivals: StationRemark[];
    departures: StationRemark[];
    rekeys: StationRemark[];
    outages: OutageRemark[];
  };
  // Per-category totals for the (now externalized) remark lists.
  auto_remarks_summary?: {
    arrivals: number;
    departures: number;
    rekeys: number;
    outages: number;
    total: number;
  };
  errata: ArchiveErratum[];
}

// The whole-archive cross-corpus rollup (content_type = "all"). Distinct
// shape from a per-corpus ArchiveDescription — the shared dimensions only,
// counted as documents (broadcast pages + podcast episodes).
export interface CrossSetSourceType {
  source_type: string;
  documents: number;
  superdocuments: number | null;
  duration_hours: number;
}

export interface CrossSetSource {
  source_id: string;
  source_name: string | null;
  source_type: string;
  documents: number;
}

export interface CrossSetDescription {
  content_type: "all";
  corpora: string[];
  totals: {
    documents: number;
    superdocuments: number | null;
    distinct_sources: number;
  };
  by_source_type: CrossSetSourceType[];
  by_source: CrossSetSource[];
  by_source_truncated: boolean;
  by_category: { category: string | null; documents: number }[];
  // Per-month document counts split by corpus (0-filled per corpus, month-
  // sorted) for the cross-corpus timeline chart. broadcast = pages, podcast =
  // episodes; the keys present are the corpora in `corpora`.
  timeline: CrossSetTimelinePoint[];
}

export interface CrossSetTimelinePoint {
  month: string; // "YYYY-MM"
  broadcast?: number;
  podcast?: number;
}

export type ArchiveDoc = ArchiveDescription | CrossSetDescription;

export function isCrossSet(doc: ArchiveDoc): doc is CrossSetDescription {
  return doc.content_type === "all";
}

// Content_type is a query param on every archive route (default broadcast).
// Omit for broadcast to keep URLs clean; "all" fetches the cross-set doc.
function ctQuery(contentType?: string): string {
  return contentType && contentType !== "broadcast"
    ? `?content_type=${encodeURIComponent(contentType)}`
    : "";
}

export function getArchiveDescription(contentType?: string): Promise<ArchiveDoc> {
  return getJson<ArchiveDoc>(`/api/v1/operator/archive/description${ctQuery(contentType)}`);
}

// One category bucket of station-lifecycle remarks. arrivals/departures/rekeys
// are StationRemark; outages are OutageRemark. All carry `.text` (pre-rendered).
export type Remark = StationRemark | OutageRemark;
export type RemarkCategory = "arrivals" | "departures" | "rekeys" | "outages";

export interface ArchiveRemarksResponse {
  content_type: string;
  category: RemarkCategory | null;
  offset: number;
  limit: number;
  total: number; // total rows matching the (optional) category filter
  counts: Record<RemarkCategory, number>; // per-category totals, ignoring filter
  remarks: Remark[];
}

// Query string for the paged remarks endpoint. content_type follows the
// clean-URL convention (omitted for broadcast); category/offset/limit are
// appended when meaningful. Exported for unit testing.
export function remarksQuery(
  contentType?: string,
  category?: RemarkCategory,
  offset = 0,
  limit = 50,
): string {
  const p = new URLSearchParams();
  if (contentType && contentType !== "broadcast") p.set("content_type", contentType);
  if (category) p.set("category", category);
  if (offset) p.set("offset", String(offset));
  p.set("limit", String(limit));
  return `?${p.toString()}`;
}

export function getArchiveRemarks(
  contentType?: string,
  category?: RemarkCategory,
  offset = 0,
  limit = 50,
): Promise<ArchiveRemarksResponse> {
  return getJson<ArchiveRemarksResponse>(
    `/api/v1/operator/archive/remarks${remarksQuery(contentType, category, offset, limit)}`,
  );
}

// Page through an entire category (used where a full set is needed, e.g. the
// per-year arrival/departure counts). Bounded for broadcast; empty for podcast
// (lifecycle suppressed). Loops in case the endpoint caps `limit`.
export async function getAllArchiveRemarks(
  contentType: string,
  category: RemarkCategory,
): Promise<Remark[]> {
  const PAGE = 1000;
  const out: Remark[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const r = await getArchiveRemarks(contentType, category, offset, PAGE);
    out.push(...r.remarks);
    if (r.remarks.length < PAGE || out.length >= r.total) break;
  }
  return out;
}

export interface MonthNotesResponse {
  content_type: string;
  year: string;
  month: string;
  notes: MonthNote[];
  updated_at: number | null;
}

export function getMonthNotes(
  year: string,
  month: string,
  contentType?: string,
): Promise<MonthNotesResponse> {
  return getJson<MonthNotesResponse>(
    `/api/v1/operator/archive/${year}/${month}/notes${ctQuery(contentType)}`,
  );
}

// One station's slice of a month (the per-month _description.json's
// by_network entries — richer than the archive-level ArchiveNetwork).
export interface MonthNetwork {
  source_id: string;
  source_name: string | null;
  pages: number;
  duration_hours: number;
  days_present: number;
}

// The full per-month _description.json, served from the operator cache at
// /api/v1/operator/archive/{year}/{month}/description. The drill-down year
// page fetches the 12 months and reads coverage.per_day_pages (daily
// heatmap) plus the per-month network / category / media breakdowns.
export interface MonthDescription {
  meta: { content_type: string; year: string; month: string; generated_at?: string };
  totals: {
    pages: number;
    duration_hours: number;
    superdocuments: number | null;
    distinct_stations: number;
  };
  coverage: {
    first_utc: string | null;
    last_utc: string | null;
    days_in_month: number;
    days_present: number;
    missing_days: string[]; // ["YYYY-MM-DD", ...]
    per_day_pages: Record<string, number>; // {"YYYY-MM-DD": pages}
  };
  by_network: MonthNetwork[];
  by_category: ArchiveCategory[];
  by_media_type: Record<string, number>; // {media_type: page count}
  station_roster: { source_id: string; source_name: string | null; pages: number }[];
  // Podcast-only (null on broadcast).
  by_language?: ArchiveLanguage[] | null;
  by_explicit?: Record<string, number> | null;
}

export function getMonthDescription(
  year: string,
  month: string,
  contentType?: string,
): Promise<MonthDescription> {
  return getJson<MonthDescription>(
    `/api/v1/operator/archive/${year}/${month}/description${ctQuery(contentType)}`,
  );
}

export async function putMonthNotes(
  year: string,
  month: string,
  notes: MonthNote[],
  contentType?: string,
): Promise<MonthNotesResponse> {
  const res = await fetchWithAuth(
    `/api/v1/operator/archive/${year}/${month}/notes${ctQuery(contentType)}`,
    {
      method: "PUT",
      body: JSON.stringify({ notes }),
    },
  );
  if (!res.ok) {
    throw new ApiError(res.status, await readBody(res), `notes PUT returned ${res.status}`);
  }
  return (await res.json()) as MonthNotesResponse;
}

// Enqueue describe-archive Job(s). Pass {year,month} for one month,
// {all:true} for every rollup'd month, or {stale:true} for just the
// months whose cached description is missing/outdated. One Job per month.
export interface DescribeRequest {
  year?: string;
  month?: string;
  all?: boolean;
  stale?: boolean;
  content_type?: string;
}

export interface DescribeResponse {
  count: number;
  submitted: { month: string; job_id: string }[];
}

export async function describeArchive(req: DescribeRequest): Promise<DescribeResponse> {
  const res = await fetchWithAuth("/api/v1/operator/archive/describe", {
    method: "POST",
    body: JSON.stringify(req),
  });
  if (res.status !== 202) {
    throw new ApiError(res.status, await readBody(res), `archive/describe returned ${res.status}`);
  }
  return (await res.json()) as DescribeResponse;
}
